import React, { useState, useEffect } from 'react';
import Stack from '@mui/material/Stack';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Badge from '@mui/material/Badge';
import Divider from '@mui/material/Divider';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Grid from '@mui/material/Grid2';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import dayjs from 'dayjs';
import 'dayjs/locale/de'; // Importiere die deutsche Locale
import { ThemeProvider } from '@mui/material/styles';
import { theme } from './ColorTheme';
import { generateSlides } from './utils';
import { useMatomo } from '@datapunt/matomo-tracker-react'

dayjs.locale('de'); // Setze die Locale auf Deutsch

const bull = (
  <Box
    component="span"
    sx={{ display: 'inline-block', mx: '2px', transform: 'scale(0.8)' }}
  >
    •
  </Box>
);

export const MenuComponent = () => {
  const { trackPageView } = useMatomo()

  // Track page view
  React.useEffect(() => {
    trackPageView({ documentTitle: 'Speisekarte' })
  }, [])

  const [activeView, setActiveView] = useState('tagesmenue'); // "tagesmenue" or "speisekarte"
  const [dailyMenus, setDailyMenus] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDailyMenus = async () => {
      const daysToFetch = 7;
      const today = dayjs();
      const menuData = {};

      try {
        setLoading(true);
        for (let i = 0; i < daysToFetch; i++) {
          const date = today.add(i, 'day').format('YYYY-MM-DD');
          const response = await fetch(`https://baeren-table-api.cr.baeren-lechtal.com/menu/${date}`);
          if (!response.ok) throw new Error(`Fehler beim Abrufen der Menüdaten für ${date}.`);
          const data = await response.json();
          menuData[date] = data.menus ? data.menus : [];
        }
        setDailyMenus(menuData);

        // Check if all daily menus are empty
        const hasAnyMenus = Object.values(menuData).some(menus => menus.length > 0);
        if (!hasAnyMenus) {
          // Redirect to PDF if no daily menus available
          window.location.href = 'https://www.holzgau.net/storage/2026/01/Speisekarte-Winter-25-26.pdf';
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchDailyMenus();
  }, []);

  return (
    <div className="content padded">
      <img
        src="logo.svg"
        alt="Gasthof Bären Logo"
        style={{
          width: "150px",
          marginBottom: "30px",
          filter: "drop-shadow(0 4px 12px rgba(75, 148, 156, 0.2))"
        }}
      />
      <ThemeProvider theme={theme}>
        <Stack spacing={2} mb={"30px"} direction="row" justifyContent={"center"}>
          <Button
            color={"baerendark"}
            variant={activeView === 'tagesmenue' ? "contained" : "outlined"}
            onClick={() => setActiveView('tagesmenue')}
            sx={{
              borderRadius: '24px',
              padding: '10px 24px',
              fontSize: '14px',
              fontWeight: 600,
              textTransform: 'none',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-2px)',
              }
            }}
          >
            📅 Tagesmenü
          </Button>
          <Button
            color={"baerendark"}
            variant="outlined"
            onClick={() => window.open('https://www.holzgau.net/storage/2026/01/Speisekarte-Winter-25-26.pdf', '_blank')}
            sx={{
              borderRadius: '24px',
              padding: '10px 24px',
              fontSize: '14px',
              fontWeight: 600,
              textTransform: 'none',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-2px)',
              }
            }}
          >
            📖 Speisekarte
          </Button>
        </Stack>

        {activeView === 'tagesmenue' && (
          Object.entries(dailyMenus).map(([day, slides]) => (
            <Accordion
              key={day}
              sx={{
                marginBottom: '12px',
                borderRadius: '12px !important',
                '&:before': { display: 'none' },
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                '&:hover': {
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12)',
                }
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  backgroundColor: 'rgba(75, 148, 156, 0.05)',
                  borderRadius: '12px',
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#324b4e' }}>
                  {dayjs(day).format('dddd, DD. MMMM YYYY')}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ padding: '16px' }}>
                {slides.length === 0 ? (
                  <Typography>Kein Menü für diesen Tag hinterlegt.</Typography>
                ) : (
                  <Grid size={{ xs: 12 }}>
                    <Swiper
                      modules={[Navigation, Pagination]}
                      spaceBetween={50}
                      slidesPerView={1}
                      navigation
                      pagination={{ clickable: true }}
                      autoHeight={true}
                    >
                      {Object.entries(generateSlides(slides, true)).map(([key, slide]) => (
                        <SwiperSlide key={slide.id}>
                          <MenuEntry slide={slide} />
                        </SwiperSlide>
                      ))}
                    </Swiper>
                  </Grid>
                )}
              </AccordionDetails>
            </Accordion>
          )))}
        {false && activeView === 'speisekarte' && (
          <>
            <Accordion>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
              >
                <Typography variant="subtitle1">
                  Vorne weg
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ padding: '20px' }}>
                <Typography variant="h6" mb={"20px"} sx={{ fontFamily: "'Alex Brush', cursive" }}>{bull}Suppen{bull}</Typography>
                <Typography variant="body1">Rindsbouillon mit Kräuterfrittaten oder Backerbsen</Typography>
                <Divider sx={{ margin: '10px 0' }} />
                <Typography>Kraftbrühe vom Hirsch mit Leberknödel</Typography>
                <Divider sx={{ margin: '10px 0' }} />
                <Typography>hausgemachte Gulaschsuppe mit Brot</Typography>

                <Typography variant="h6" mt={"40px"} mb={"20px"} sx={{ fontFamily: "'Alex Brush', cursive" }}>{bull}Vorspeisen{bull}</Typography>
                <Typography variant="body1">Carpaccio vom Rind an kleinem Caesar-Salat, mit gehobeltem Parmesan, Senfcreme, Toastbrot und Butter</Typography>
                <Divider sx={{ margin: '10px 0' }} />
                <Typography variant="body1">geräuchertes Forellenfilet mit Kürbis-Fichten-Apfelgelee, hausgemachtes Brot</Typography>
                <Divider sx={{ margin: '10px 0' }} />
                <Typography variant="body1">Portion Salat vom Buffet</Typography>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1">Kinderteller</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body1">"Goofy-Teller" kleines Wiener Schnitzel mit Pommes frites, Ketchup und Gemüse</Typography>
                <Divider sx={{ margin: '10px 0' }} />
                <Typography variant="body1">"Sponge Bob" Spätzle mit Sauce</Typography>
                <Divider sx={{ margin: '10px 0' }} />
                <Typography variant="body1">"Rote Hexe" Spaghetti mit Tomatenfleischsauce</Typography>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1">Mitten drin</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="h6" mb={"20px"} sx={{ fontFamily: "'Alex Brush', cursive" }}>{bull}Bären-Klassiker{bull}</Typography>
                <Typography variant="body1">Wiener Schnitzel (vom Schwein) mit Preiselbeeren, Pommes frites</Typography>
                <Divider sx={{ margin: '10px 0' }} />
                <Typography variant="body1">Schweinerückensteak auf Pfeffersauce, dazu Kartoffelkroketten</Typography>
                <Divider sx={{ margin: '10px 0' }} />
                <Typography variant="body1">Wiener Tafelspitz (vom Rind) mit Röstkartoffel, Cremespinat und Semmelkren</Typography>
                <Divider sx={{ margin: '10px 0' }} />
                <Typography variant="body1">Bären-Pfandl (Rumpsteak und Schweinefilet) mit Butterspätzle, dazu Preiselbeeren und Pilzrahm</Typography>
                <Divider sx={{ margin: '10px 0' }} />
                <Typography variant="body1">Grillteller (Rumpsteak, Schweinefilet, Würstl, Speck) mit Kräuterbutter und Cocktailsauce, dazu Pommes frites</Typography>
                <Divider sx={{ margin: '10px 0' }} />
                <Typography variant="body1">Tiroler Leber (geröstete Schweinsleber mit Speck), dazu Salzkartoffeln</Typography>
                <Divider sx={{ margin: '10px 0' }} />
                <Typography variant="body1">Zwiebelrostbraten mit Speckbohnen, serviert mit Röstkartoffeln</Typography>
                <Divider sx={{ margin: '10px 0' }} />
                <Typography variant="body1">Duo vom Reh (Ragout und kurz gebratenes von der Keule) dazu Butterspätzle und Preiselbeeren</Typography>
                <Typography variant="h6" mt={"40px"} mb={"20px"} sx={{ fontFamily: "'Alex Brush', cursive" }}>{bull}Fleischlos{bull}</Typography>
                <Divider sx={{ margin: '10px 0' }} />
                <Typography variant="body1">Lechtaler Käsespätzle mit Röstzwiebel</Typography>
                <Divider sx={{ margin: '10px 0' }} />
                <Typography variant="body1">Kichererbsenbowl mit Curry, Pak Choi und Nüssen, hausgemachtes Gözleme (türkisches Fladenbrot)</Typography>
                <Divider sx={{ margin: '10px 0' }} />
                <Typography variant="body1">Süßkartoffel aus dem Ofen mit Linsen-Dal, Harissa, geröstetet Cashewnüsse und Minzjoghurt</Typography>
                <Divider sx={{ margin: '10px 0' }} />
                <Typography variant="body1">hausgemachte Strozzapreti (Pasta) mit Pilzrahmsauce</Typography>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1">Zum Schluss</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="h6" mb={"20px"} sx={{ fontFamily: "'Alex Brush', cursive" }}>{bull}Süßes{bull}</Typography>
                <Typography variant="body1">Germknödel mit zerlassener Butter und Mohn oder Vanillesauce und Mohn</Typography>
                <Divider sx={{ margin: '10px 0' }} />
                <Typography variant="body1">Warmer Apfelstrudel Spezialität des Hauses</Typography>
                <Divider sx={{ margin: '10px 0' }} />
                <Typography variant="body1">Kaiserschmarren aus Freilandeiern dazu Apfelmus (ohne Rosinen)</Typography>
                <Divider sx={{ margin: '10px 0' }} />
                <Typography variant="body1">Maronen-Pistazien-Parfait mit Himbeeren und Meringe</Typography>
                <Divider sx={{ margin: '10px 0' }} />
                <Typography variant="body1">Waldbeeren unter der gebrannten Topfenhaube mit Vanilleeis und Kürbiskernöl</Typography>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1">Auf Vorbestellung</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body1">Schweinehaxe gebraten mit Kraut, Knödel und Salzkartoffeln oder Pommes frites und Salat vom Buffet</Typography>
                <Divider sx={{ margin: '10px 0' }} />
                <Typography variant="body1">Burgunder Fondue (mit Öl) - Rindsfilet; dazu verschiedene Saucen: Tartaresauce, Amerikanische Sauce, Knoblauchsauce, Currysauce, Zigeunersauce, Gurken, Perlzwiebel, uvm.</Typography>
                <Divider sx={{ margin: '10px 0' }} />
                <Typography variant="body1">Fondue Chinoise (mit Brühe) - Rinds-, Schweine-, und Hühnerfilet; dazu verschiedene Saucen</Typography>
                <Divider sx={{ margin: '10px 0' }} />
                <Typography variant="body1">Käsefondue serviert mit Weißbrotwürfeln, Gurken, Oliven, Perlzwiebel, uvm.</Typography>
              </AccordionDetails>
            </Accordion>
          </>
        )}

      </ThemeProvider>
    </div>
  );
};

export const MenuEntry = ({ slide }) => {
  let badgeData = [0, "primary"];
  if (slide.specialoffer) badgeData = ["Empfehlung", "baerendark"];
  if (slide.vegetarian) badgeData = ["Vegetarisch", "baerengruen"];

  return (
    <Paper
      elevation={6}
      sx={{
        margin: "15px",
        marginBottom: "45px",
        width: "70%",
        marginRight: "auto",
        marginLeft: "auto",
        borderRadius: '16px',
        overflow: 'hidden',
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 12px 28px rgba(0, 0, 0, 0.15)',
        }
      }}
    >
      <Badge
        sx={{
          "& .MuiBadge-badge": {
            fontSize: 15,
            padding: '8px 12px',
            borderRadius: '12px',
          }
        }}
        badgeContent={badgeData[0]}
        color={badgeData[1]}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Card sx={{ minWidth: 200, boxShadow: 'none' }}>
          <CardContent sx={{ padding: '24px' }}>
            <Typography
              variant="h5"
              sx={{
                color: '#324b4e',
                marginBottom: '16px',
                fontFamily: "'Alex Brush', cursive",
                fontSize: '26px',
              }}
            >
              {slide.title}
              {(slide.price && false) &&
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Preis pro Person {Number(slide.price).toFixed(2)}€
                </Typography>
              }
            </Typography>

            {slide.courses.map((course, index) => (
              <React.Fragment key={index}>
                {course.coursetitle && (
                  <Typography variant="h6" sx={{ fontFamily: "'Alex Brush', cursive" }} component="div">
                    {course.coursetitle}
                  </Typography>
                )}
                {course.coursesubtitle && (
                  <Typography variant="body2" sx={{ color: 'text.primary', mb: 1.0 }} component="div">
                    {course.coursesubtitle}
                  </Typography>
                )}
                {course.coursedesc && (
                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
                    {course.coursedesc}
                  </Typography>
                )}
                {index < slide.courses.length - 1 && (
                  <Divider sx={{ borderBottomWidth: 1, margin: 1 }} />
                )}
              </React.Fragment>
            ))}
          </CardContent>
        </Card>
      </Badge>
    </Paper>
  );
};